import {
  ContentLengthSchema,
  NotANumberAndRequiredSchema,
  NotANumberSchema,
  NumberAndRequiredSchema,
} from './common-validator-schemas';
// eslint-disable-next-line import/extensions
import USStatesUtInfo from './us-states-ut-info.json';
import { OptionalString } from '@/app/core';
import * as yup from 'yup';

const {
  codesRegexString,
  namesRegexString,
  nameTokensWithLookbacks,
}: {
  codesRegexString: string;
  namesRegexString: string;
  nameTokensWithLookbacks: Record<string, number>;
} = USStatesUtInfo;
// export const getStatesAndUTInfo = (): any => {};

const codesRegex = new RegExp(codesRegexString, 'i');
const namesRegex = new RegExp(namesRegexString, 'i');

export const getAddressLineParts = (addressLine: OptionalString): string[] => {
  return (addressLine || '').split(/[,\s]+/gi).map((a) => a.trim());
};
export const extractStateOrUTNameAndCity = (
  reversedAddressParts: string[],
): {
  stateOrUt: OptionalString;
  cityName: OptionalString;
} => {
  const [lastPart, ...rest] = reversedAddressParts;
  if (!lastPart) {
    return { stateOrUt: null, cityName: null };
  } else {
    const lookBackCount = nameTokensWithLookbacks[lastPart.toLowerCase()];
    if (!lookBackCount) {
      return {
        stateOrUt: lastPart,
        cityName: rest.reverse().join(' '),
      };
    } else {
      const buildArr: string[] = [];
      buildArr.push(lastPart);
      for (let i = 0; i < lookBackCount; i++) {
        buildArr.push(rest[i]);
      }
      return {
        stateOrUt: buildArr.reverse().join(' '),
        cityName: rest.slice(lookBackCount).reverse().join(' '),
      };
    }
  }
};

export const HasZipAndStateAndCitySchema = (
  canCityPartHaveNumbers: boolean,
): yup.StringSchema<string | undefined> =>
  yup.string().test({
    name: 'HasZipAndStateAndCity',
    test(value: OptionalString) {
      const [zipCode, ...rest] = getAddressLineParts(value).reverse();
      if (!/^[0-9]{5}$/.test(zipCode)) {
        return this.createError({
          message: `${zipCode} is invalid.`,
        });
      } else {
        try {
          HasStateAndCitySchema(canCityPartHaveNumbers).validateSync(
            rest.reverse().join(' '),
          );
          return true;
        } catch (err) {
          return err;
        }
      }
    },
  });

export const HasZipAndStateSchema: yup.StringSchema<string | undefined> = yup
  .string()
  .test({
    name: 'HasZipAndState',
    test(value: OptionalString) {
      if (!value) {
        return true;
      }
      const trimmedValue = value.trim();
      const commaLastIndex = trimmedValue.lastIndexOf(',');
      const spaceLastIndex = trimmedValue.lastIndexOf(' ');
      const splitPosition =
        commaLastIndex > spaceLastIndex ? commaLastIndex : spaceLastIndex;
      const zipCodeString = (
        trimmedValue.substring(splitPosition + 1)?.trim() || ''
      ).replace(/^[,\s]+/, '');
      const stateCodeString = (
        trimmedValue.substring(0, splitPosition)?.trim() || ''
      ).replace(/[,\s]+$/, '');
      // console.log(
      //   'stateCodeString: ',
      //   stateCodeString,
      //   ':',
      //   zipCodeString,
      //   ':',
      //   commaLastIndex,
      //   ':',
      //   spaceLastIndex,
      // );
      const messages: string[] = [];
      if (!/\d{3,5}/.test(zipCodeString)) {
        messages.push(`"${zipCodeString}" is not a valid Zip Code`);
      }
      if (
        !(codesRegex.test(stateCodeString) || namesRegex.test(stateCodeString))
      ) {
        messages.push(
          `"${stateCodeString}" is not a valid State/UT name or code`,
        );
      }
      return messages.length
        ? this.createError({
            message: messages.join(', '),
          })
        : true;
    },
  });

export const HasStateAndCitySchema = (
  canCityPartHaveNumbers: boolean,
): yup.StringSchema<string | undefined> =>
  yup.string().test({
    name: 'HasStateAndCity',
    test(value: OptionalString) {
      const info = extractStateOrUTNameAndCity(
        getAddressLineParts(value).reverse(),
      );
      const messages: string[] = [];
      if (!info.stateOrUt) {
        messages.push('Could not find State/UT.');
      } else {
        if (
          !(codesRegex.test(info.stateOrUt) || namesRegex.test(info.stateOrUt))
        ) {
          messages.push(
            `${info.stateOrUt} is not a valid State/UT name or code`,
          );
        }
      }
      if (!info.cityName) {
        messages.push('Could not find City name.');
      } else if (!canCityPartHaveNumbers && /[0-9]/.test(info.cityName)) {
        messages.push('City name should not contain any numbers.');
      }
      return messages.length
        ? this.createError({
            message: messages.join(', '),
          })
        : true;
    },
  });

const addressValidationsTemplate: Record<string, yup.StringSchema> = {
  Addr1_Apt: ContentLengthSchema('Addr1_Apt'),
  Addr1_City: ContentLengthSchema('Addr1_City').concat(NotANumberSchema),
  Addr1_City_St: ContentLengthSchema('Addr1_City_St')
    .concat(NotANumberSchema)
    .concat(HasStateAndCitySchema(false))
    .required(),
  Addr1_City_St_ZIP5: ContentLengthSchema('Addr1_City_St_ZIP5')
    .concat(HasZipAndStateAndCitySchema(true))
    .required(),
  Addr1_Full: ContentLengthSchema('Addr1_Full')
    .concat(HasZipAndStateAndCitySchema(true))
    .required(),
  Addr1_House_No: ContentLengthSchema('Addr1_House_No').concat(
    NumberAndRequiredSchema,
  ),
  Addr1_Line1: ContentLengthSchema('Addr1_Line1').min(3).required(),
  Addr1_Line2: ContentLengthSchema('Addr1_Line2'),
  Addr1_POBOX: ContentLengthSchema('Addr1_POBOX'),
  Addr1_Post_Dir:
    ContentLengthSchema('Addr1_Post_Dir').concat(NotANumberSchema),
  Addr1_Pre_Dir: ContentLengthSchema('Addr1_Pre_Dir').concat(NotANumberSchema),
  Addr1_St_Suffix:
    ContentLengthSchema('Addr1_St_Suffix').concat(NotANumberSchema),
  Addr1_St_ZIP5: ContentLengthSchema('Addr1_St_ZIP5')
    .concat(HasZipAndStateSchema)
    .required(),
  Addr1_State: ContentLengthSchema('Addr1_State')
    .concat(NotANumberSchema)
    .test({
      name: 'IsAValidState',
      test(value: OptionalString) {
        if (!value) {
          return this.createError({
            message: 'State/UT cannot be blank.',
          });
        } else {
          if (!(codesRegex.test(value) || namesRegex.test(value))) {
            return this.createError({
              message: `${value} is not a valid State/UT name or code`,
            });
          }
          return true;
        }
      },
    }),
  Addr1_Street: ContentLengthSchema('Addr1_Street').required().min(1),
  Addr1_ZIP5: ContentLengthSchema('Addr1_ZIP5')
    .concat(NumberAndRequiredSchema)
    .min(3)
    .max(5),
  Addr1_ZIP5_No_Zero_Fill: ContentLengthSchema('Addr1_ZIP5_No_Zero_Fill')
    .concat(NotANumberAndRequiredSchema)
    .min(3)
    .max(5),
  Addr1_ZIP9: ContentLengthSchema('Addr1_ZIP9').matches(
    /^(\d{7,9}|\d{3,5}|\d{5}-\d{4})$/i,
    'Zip Code value: "${value}" is invalid.',
  ),
};

export const generateAddressFieldValidations = (
  newPrefix: string,
): Record<string, yup.StringSchema<string | undefined>> =>
  Object.entries(addressValidationsTemplate).reduce((acc, entry) => {
    const [key, value] = entry;
    const newKey = key.replace('Addr1_', newPrefix);
    acc[newKey.toLowerCase()] = value.label(newKey);
    return acc;
  }, {} as Record<string, yup.StringSchema>);

export const addressFieldValidators: Record<string, yup.StringSchema> = {
  ...generateAddressFieldValidations('Addr1_'),
  ...generateAddressFieldValidations('Addr2_'),
  ...generateAddressFieldValidations('Addr3_'),
};
